import moment from 'moment';
import { FaArrowLeft, FaRedo } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import {
    Badge, Button, Card, CardBody, CardHeader,
    Col, Container, Progress, Row, Spinner, Table
} from 'reactstrap';
import useJobDetails from './customHooks/useJobDetails';

const statusColor = (s) => {
    if (!s) return 'secondary';
    const v = s.toLowerCase();
    if (v === 'completed') return 'success';
    if (v === 'failed') return 'danger';
    if (v === 'processing') return 'primary';
    if (v === 'pending') return 'warning';
    return 'secondary';
};

const StatItem = ({ label, value, color }) => (
    <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-2 border-0 border-bottom">
        <span className="text-muted small">{label}</span>
        <span className={`fw-bold ${color ? `text-${color}` : ''}`}>{value ?? '—'}</span>
    </li>
);

const SectionCard = ({ title, headerClass = 'bg-white fw-semibold', children }) => (
    <Card className="shadow-sm border-0 mb-4">
        <CardHeader className={headerClass}>{title}</CardHeader>
        <CardBody className="py-2 px-3">{children}</CardBody>
    </Card>
);

const ChunkBar = ({ stats }) => {
    if (!stats || stats.total === 0) return <p className="text-muted small mb-0">No chunk data</p>;
    const pct = (val) => ((val / stats.total) * 100).toFixed(0);
    return (
        <div>
            <div className="d-flex justify-content-between small mb-1">
                <span>Total: <strong>{stats.total}</strong></span>
                <span className="text-success">Done: {stats.completed}</span>
                <span className="text-danger">Failed: {stats.failed}</span>
                <span className="text-warning">Pending: {stats.pending}</span>
                <span className="text-primary">Processing: {stats.processing}</span>
            </div>
            <Progress multi style={{ height: '10px' }}>
                <Progress bar color="success" value={pct(stats.completed)} />
                <Progress bar color="danger" value={pct(stats.failed)} />
                <Progress bar color="warning" value={pct(stats.pending)} />
                <Progress bar color="primary" value={pct(stats.processing)} />
            </Progress>
        </div>
    );
};

const SyncServiceStats = ({ label, stats }) => {
    if (!stats) return null;
    return (
        <div className="mb-3">
            <p className="fw-semibold mb-1 small text-uppercase text-muted">{label}</p>
            <div className="d-flex gap-2 flex-wrap">
                {['total', 'completed', 'pending', 'processing', 'failed'].map(key => (
                    <Badge
                        key={key}
                        color={key === 'completed' ? 'success' : key === 'failed' ? 'danger' : key === 'processing' ? 'primary' : 'secondary'}
                        className="py-1 px-2"
                    >
                        {key}: {stats[key] ?? 0}
                    </Badge>
                ))}
            </div>
        </div>
    );
};

const DnhJobDetails = () => {
    const history = useHistory();
    const { id } = useParams();
    const { jobDetails, loading, fetchDetails } = useJobDetails(id);

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <Spinner color="primary" />
            </Container>
        );
    }

    if (!jobDetails) {
        return (
            <Container className="text-center py-5">
                <h4>Job not found</h4>
                <Button color="primary" onClick={() => history.push('/admin/dnh-jobs')}>Back to Jobs</Button>
            </Container>
        );
    }

    const {
        results, timestamps, chunks, failedChunks,
        sync, images, progress, totals, config,
        rollbackResults, error, syncStatus
    } = jobDetails;

    const duration = timestamps?.startedAt && timestamps?.completedAt
        ? moment.duration(moment(timestamps.completedAt).diff(moment(timestamps.startedAt))).humanize()
        : null;

    const progressPct = progress?.percentage ?? (
        totals?.totalProducts && totals?.processedProducts
            ? Math.round((totals.processedProducts / totals.totalProducts) * 100)
            : null
    );

    return (
        <Container fluid className="p-4">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center gap-3">
                    <Button color="link" className="text-decoration-none text-dark p-0" onClick={() => history.goBack()}>
                        <FaArrowLeft size={20} />
                    </Button>
                    <div>
                        <h4 className="mb-0">Job Details</h4>
                        <small className="text-muted">#{id}</small>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Badge color={statusColor(jobDetails.status)} className="px-3 py-2 fs-6">
                        {jobDetails.status || 'Unknown'}
                    </Badge>
                    <Button color="outline-secondary" size="sm" onClick={() => fetchDetails(id)}>
                        <FaRedo size={12} className="me-1" /> Refresh
                    </Button>
                </div>
            </div>

            {/* Overall Progress */}
            {progressPct !== null && (
                <Card className="mb-4 shadow-sm border-0">
                    <CardBody>
                        <div className="d-flex justify-content-between mb-1">
                            <span className="fw-semibold">Overall Progress</span>
                            <span className="fw-bold">{progressPct}%</span>
                        </div>
                        <Progress value={progressPct} color={progressPct === 100 ? 'success' : 'primary'} style={{ height: '12px' }} />
                        {progress?.currentChunk && (
                            <small className="text-muted mt-1 d-block">
                                Chunk {progress.currentChunk} of {progress.totalChunks}
                                {progress.currentOperation && ` — ${progress.currentOperation}`}
                            </small>
                        )}
                    </CardBody>
                </Card>
            )}

            <Row>
                {/* Left Column */}
                <Col md="4">
                    {/* Job Info */}
                    <SectionCard title="Job Information">
                        <ul className="list-unstyled mb-0">
                            <StatItem label="Title" value={jobDetails.title} />
                            <StatItem label="Type" value={jobDetails.type} />
                            <StatItem label="Job ID" value={jobDetails.id ?? jobDetails.jobId} />
                            <StatItem label="Created At" value={timestamps?.createdAt ? moment(timestamps.createdAt).format('lll') : '—'} />
                            <StatItem label="Started At" value={timestamps?.startedAt ? moment(timestamps.startedAt).format('lll') : '—'} />
                            <StatItem label="Completed At" value={timestamps?.completedAt ? moment(timestamps.completedAt).format('lll') : '—'} />
                            {duration && <StatItem label="Duration" value={duration} />}
                        </ul>
                    </SectionCard>

                    {/* Totals */}
                    {totals && Object.keys(totals).length > 0 && (
                        <SectionCard title="Totals">
                            <ul className="list-unstyled mb-0">
                                {Object.entries(totals).map(([k, v]) => (
                                    <StatItem key={k} label={k.replace(/([A-Z])/g, ' $1').trim()} value={v} />
                                ))}
                            </ul>
                        </SectionCard>
                    )}

                    {/* Config */}
                    {config && Object.keys(config).length > 0 && (
                        <SectionCard title="Configuration">
                            <ul className="list-unstyled mb-0">
                                {Object.entries(config).map(([k, v]) => (
                                    <StatItem key={k} label={k.replace(/([A-Z])/g, ' $1').trim()} value={typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)} />
                                ))}
                            </ul>
                        </SectionCard>
                    )}

                    {/* Job Statistics (results) */}
                    {results && (
                        <SectionCard title="Processing Results">
                            <ul className="list-unstyled mb-0">
                                {[
                                    ['Processed', results.processed, null],
                                    ['Created', results.created, 'success'],
                                    ['Updated', results.updated, 'primary'],
                                    ['Unchanged', results.unchanged, null],
                                    ['Skipped', results.skipped, null],
                                    ['Price Changes', results.priceChanges, 'warning'],
                                    ['Quantity Changes', results.quantityChanges, 'warning'],
                                    ['Errors', results.errors?.length, 'danger'],
                                ].map(([label, value, color]) => value !== undefined && value !== null ? (
                                    <StatItem key={label} label={label} value={value} color={color} />
                                ) : null)}
                            </ul>
                        </SectionCard>
                    )}

                    {/* Rollback Results */}
                    {rollbackResults && (
                        <SectionCard title="Rollback Results" headerClass="bg-warning bg-opacity-10 fw-semibold">
                            <ul className="list-unstyled mb-0">
                                {Object.entries(rollbackResults).map(([k, v]) => (
                                    <StatItem key={k} label={k.replace(/([A-Z])/g, ' $1').trim()} value={String(v)} />
                                ))}
                            </ul>
                        </SectionCard>
                    )}
                </Col>

                {/* Right Column */}
                <Col md="8">
                    {/* Error */}
                    {error && (
                        <Card className="shadow-sm border-0 mb-4 border-danger">
                            <CardHeader className="bg-danger text-white fw-semibold">Job Error</CardHeader>
                            <CardBody>
                                <code className="text-danger">{typeof error === 'object' ? JSON.stringify(error, null, 2) : error}</code>
                            </CardBody>
                        </Card>
                    )}

                    {/* Chunk Stats */}
                    {chunks && (
                        <SectionCard title={`Chunk Processing (${chunks.total} total)`}>
                            <ChunkBar stats={chunks} />
                            <div className="d-flex gap-3 mt-2 flex-wrap">
                                <small><span className="text-success fw-bold">{chunks.completed}</span> completed</small>
                                <small><span className="text-danger fw-bold">{chunks.failed}</span> failed</small>
                                <small><span className="text-primary fw-bold">{chunks.processing}</span> processing</small>
                                <small><span className="text-warning fw-bold">{chunks.pending}</span> pending</small>
                            </div>
                        </SectionCard>
                    )}

                    {/* Failed Chunks */}
                    {failedChunks && failedChunks.length > 0 && (
                        <SectionCard title={`Failed Chunks (${failedChunks.length})`} headerClass="bg-danger bg-opacity-10 fw-semibold text-danger">
                            <Table size="sm" responsive className="mb-0">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Chunk ID</th>
                                        <th>Products</th>
                                        <th>Retries</th>
                                        <th>Error</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {failedChunks.map((fc, idx) => (
                                        <tr key={idx}>
                                            <td>{fc.chunkNumber}</td>
                                            <td><code className="small">{fc.chunkId}</code></td>
                                            <td>{fc.productCount}</td>
                                            <td>{fc.retryCount}</td>
                                            <td className="text-danger small">{fc.error ? (typeof fc.error === 'object' ? (fc.error.message || JSON.stringify(fc.error)) : fc.error) : '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </SectionCard>
                    )}

                    {/* Sync Stats */}
                    {sync && (sync.algolia?.total > 0 || sync.quickbooks?.total > 0) && (
                        <SectionCard title="Sync Queue Status">
                            <SyncServiceStats label="Algolia" stats={sync.algolia} />
                            <SyncServiceStats label="QuickBooks" stats={sync.quickbooks} />
                        </SectionCard>
                    )}

                    {/* Image Queue */}
                    {images && images.total > 0 && (
                        <SectionCard title={`Image Queue (${images.total} total)`}>
                            <div className="d-flex gap-2 flex-wrap">
                                {['completed', 'pending', 'processing', 'failed'].map(k => (
                                    <Badge
                                        key={k}
                                        color={k === 'completed' ? 'success' : k === 'failed' ? 'danger' : k === 'processing' ? 'primary' : 'warning'}
                                        className="py-1 px-2"
                                    >
                                        {k}: {images[k] ?? 0}
                                    </Badge>
                                ))}
                            </div>
                            {images.total > 0 && (
                                <Progress
                                    className="mt-2"
                                    value={Math.round((images.completed / images.total) * 100)}
                                    color="success"
                                    style={{ height: '8px' }}
                                />
                            )}
                        </SectionCard>
                    )}

                    {/* Sync Status (from job doc itself) */}
                    {syncStatus && Object.keys(syncStatus).length > 0 && (
                        <SectionCard title="Sync Status">
                            <Table size="sm" responsive className="mb-0">
                                <thead>
                                    <tr><th>Service</th><th>Status</th><th>Detail</th></tr>
                                </thead>
                                <tbody>
                                    {Object.entries(syncStatus).map(([service, info]) => (
                                        <tr key={service}>
                                            <td className="text-capitalize">{service}</td>
                                            <td><Badge color={statusColor(info?.status)}>{info?.status || 'N/A'}</Badge></td>
                                            <td className="small text-muted">
                                                {info?.message
                                                    ? (typeof info.message === 'object' ? JSON.stringify(info.message) : info.message)
                                                    : info?.completedAt
                                                        ? moment(info.completedAt).format('lll')
                                                        : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </SectionCard>
                    )}

                    {/* Result Errors */}
                    {results?.errors?.length > 0 && (
                        <SectionCard title={`Item Errors (${results.errors.length})`} headerClass="bg-danger bg-opacity-10 fw-semibold text-danger">
                            <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                <Table striped size="sm" responsive className="mb-0">
                                    <thead>
                                        <tr><th>Item ID / SKU</th><th>Message</th></tr>
                                    </thead>
                                    <tbody>
                                        {results.errors.map((err, idx) => (
                                            <tr key={idx}>
                                                <td className="small">{err?.itemId || err?.sku || '—'}</td>
                                                <td className="small">{typeof err === 'string' ? err : err.message || JSON.stringify(err)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </SectionCard>
                    )}

                    {/* Logs */}
                    {jobDetails.logs?.length > 0 && (
                        <SectionCard title={`Job Logs (${jobDetails.logs.length})`}>
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                <Table striped size="sm" responsive className="mb-0">
                                    <thead>
                                        <tr><th style={{ width: 90 }}>Time</th><th>Level</th><th>Message</th></tr>
                                    </thead>
                                    <tbody>
                                        {jobDetails.logs.map((log, idx) => (
                                            <tr key={idx}>
                                                <td className="small text-muted">{moment(log.timestamp).format('HH:mm:ss')}</td>
                                                <td>
                                                    <Badge color={log.level === 'error' ? 'danger' : log.level === 'warn' ? 'warning' : 'info'}>
                                                        {log.level}
                                                    </Badge>
                                                </td>
                                                <td className="small">{log.message}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </SectionCard>
                    )}

                    {/* Empty state */}
                    {!results?.errors?.length && !jobDetails.logs?.length && !failedChunks?.length && !error && (
                        <Card className="shadow-sm border-0">
                            <CardBody className="text-center text-muted py-5">
                                No errors or logs to display for this job.
                            </CardBody>
                        </Card>
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default DnhJobDetails;
